import * as React from "react";
import styles from "./Buttons.module.scss";
import classnames from "classnames";
import { LoadingComponent } from "rylak-common/src/components/loadingComponent/LoadingComponent.jsx";
import useRipple from "./useRipple";
import { getBrandButtonEffects, getBrandButtonTextType, getBrandButtonThickness, getBrandButtonVariant } from "~/cores/brandDataCore";
import { BUTTON_EFFECT_OPTIONS_MAP } from "~/constants";

function ButtonContent(props) {
    const {
        onClick,
        leftIcon,
        rightIcon,
        text,
        fullWidth = false,
        type,
        disabled,
        visuallyDisabled,
        loading,
        getLocaleText,
        className = "",
        variant = "primary",
        dataTestId,
        preContent,
        postContent,
        style,
        black = false,
        brandColors = false,
        appProps,
    } = props;
    const buttonRef = React.useRef(null);
    const ripples = useRipple(buttonRef, variant === "primary" ? "white" : "white");

    const buttonBrandTextType = getBrandButtonTextType(appProps);

    const buttonBrandVariant = getBrandButtonVariant(appProps);

    const buttonBrandEffects = getBrandButtonEffects(appProps);

    const buttonBrandThickness = getBrandButtonThickness(appProps);

    return (
        <button
            onClick={onClick}
            ref={buttonRef}
            data-test-id={dataTestId}
            style={fullWidth ? { ...style, width: "100%" } : style}
            disabled={disabled || loading}
            type={type}
            className={classnames(
                styles["button"],
                visuallyDisabled && styles["visually-disabled"],
                styles["button-" + variant],
                buttonBrandVariant && styles["button-brand-variant-" + buttonBrandVariant],
                buttonBrandEffects && styles["button-brand-effects-" + buttonBrandEffects],
                preContent && styles["button-pre-content"],
                black && styles["black"],
                brandColors && styles["brand-colors"],
                buttonBrandTextType && styles[`button-text-${buttonBrandTextType}`],
                className,
            )}
        >
            {buttonBrandEffects !== BUTTON_EFFECT_OPTIONS_MAP.NO_EFFECTS.value && ripples}
            {variant !== "text" && (
                <div
                    className={styles["white-background"]}
                    style={{
                        backgroundColor: variant === "primary" ? "white" : "black",
                    }}
                />
            )}
            {preContent}
            <span className={classnames(loading && styles["button-loading"], styles["text"], leftIcon && styles["text-has-icon"])} style={{ fontWeight: buttonBrandThickness || undefined }}>
                {leftIcon && <span className={styles["leftIcon"]}>{leftIcon}</span>}
                {rightIcon && !loading && <span className={classnames(styles["leftIcon"], styles["rightIcon"])}>{rightIcon}</span>}

                {text}
            </span>
            <div className={styles["spinner"]}>{loading && <LoadingComponent getLocaleText={getLocaleText} />}</div>
            {postContent}
        </button>
    );
}

export function Button(props) {
    const { href, target } = props;

    if (href) {
        return (
            <form action={href} target={target}>
                <ButtonContent {...props} type="submit" />
            </form>
        );
    } else {
        return <ButtonContent {...props} />;
    }
}

export function ButtonRound(props) {
    return <Button {...props} className={classnames(props.className, styles["button-round"])} />;
}

export function ButtonTight(props) {
    return <Button {...props} className={classnames(props.className, styles["button-tight"])} />;
}

export function ButtonText(props) {
    return <Button {...props} variant="text" className={classnames(props.className, styles["button-text"])} />;
}

export function ButtonSquare(props) {
    return <Button {...props} className={classnames(props.className, styles["button-square"])} />;
}

export function ButtonRoundOnlyIcon(props) {
    const { onClick, icon, type, disabled, visuallyDisabled, loading, getLocaleText, className = "", dataTestId, style } = props;
    return (
        <button
            onClick={(e) => {
                e.stopPropagation();
                onClick(e);
            }}
            data-test-id={dataTestId}
            style={style}
            disabled={disabled || loading}
            type={type}
            className={classnames(styles["button-round-only-icon"], visuallyDisabled && styles["visually-disabled"], className)}
        >
            <span className={styles["center-icon"]}>{loading ? <LoadingComponent getLocaleText={getLocaleText} /> : icon}</span>
        </button>
    );
}

export function AlternatingButtons(props) {
    const { MobileButton, DesktopButton, ...rest } = props;
    return (
        <>
            <MobileButton {...rest} className={classnames(styles["mobile-button"], rest.className)} />
            <DesktopButton {...rest} className={classnames(styles["desktop-button"], rest.className)} />
        </>
    );
}
